// Runtime config resolver: maps the current hostname to the correct config bundle.
// Lets a single built artifact be deployed to stage, opt-global, opt-eu, opt-kr
// without rebuilding -- the bundle picks the right config at hydration time.

import local from './run-type/local';
import dev from './run-type/dev';
import stage from './run-type/stage';
import live from './run-type/live';
import liveEu from './run-type/live-eu';
import liveKr from './run-type/live-kr';
import liveUs from './run-type/live-us';

export type RunType = 'local' | 'dev' | 'stage' | 'live' | 'live-eu' | 'live-kr' | 'live-us';

// Shape inferred from the live config -- all run-type configs share the same shape.
export type RuntimePublicConfig = typeof live.public;

export interface RuntimeConfigBundle {
    public: RuntimePublicConfig;
}

const CONFIGS: Record<RunType, RuntimeConfigBundle> = {
    local,
    dev,
    stage,
    live,
    'live-eu': liveEu,
    'live-kr': liveKr,
    'live-us': liveUs,
};

/**
 * Resolve which run-type a hostname belongs to. Pure function -- callers pass
 * `window.location.hostname` on the client, or a known host on the server.
 * Fallback is `live` so an unrecognized prod-like host gets production config.
 */
export function resolveRunType(hostname: string): RunType {
    const host = (hostname || '').toLowerCase();

    if (host === 'localhost' || host === '127.0.0.1') return 'local';
    if (host === 'dev.codepresso.io') return 'dev';
    if (host === 'contents-stage.codepresso.io' || host.endsWith('-stage.codepresso.io')) return 'stage';
    if (host === 'eu.codepresso.io') return 'live-eu';
    if (host === 'kr.codepresso.io') return 'live-kr';
    if (host === 'us.codepresso.io') return 'live-us';
    if (host === 'codepresso.io' || host === 'www.codepresso.io') return 'live';

    return 'live';
}

/**
 * Returns the `public` portion of the runtime config matching the hostname.
 * This is what feeds into `applicationContext.setConfig(...)`.
 */
export function resolveRuntimeConfig(hostname: string): RuntimePublicConfig {
    return CONFIGS[resolveRunType(hostname)].public;
}

export interface FaroConfig {
    /** Faro/Alloy collector ingest URL. Empty string disables RUM (e.g. local). */
    url: string;
    /** `environment` label attached to every RUM signal in Grafana. */
    env: string;
}

// Faro receiver per run-type. Resolved at runtime from the hostname (same pattern
// as resolveRuntimeConfig) so ONE built artifact targets the right collector when
// reused across stage and opt -- the URL must NOT be baked at `nuxt generate` time,
// otherwise opt (which reuses the stage-built artifact) ships the stage collector
// and the browser POSTs cross-origin to faro-stage and is blocked by CORS.
// Each receiver only allows its own environment's frontend origins (see infra
// modules/observability/faro-receiver). live / live-eu / live-kr / live-us all share the
// single opt receiver (faro.codepresso.io), whose CORS allowlist covers all three.
const FARO: Record<RunType, FaroConfig> = {
    local: { url: '', env: 'local' },
    dev: { url: 'https://faro-dev.codepresso.io/collect', env: 'dev' },
    stage: { url: 'https://faro-stage.codepresso.io/collect', env: 'stage' },
    live: { url: 'https://faro.codepresso.io/collect', env: 'live' },
    'live-eu': { url: 'https://faro.codepresso.io/collect', env: 'live-eu' },
    'live-kr': { url: 'https://faro.codepresso.io/collect', env: 'live-kr' },
    'live-us': { url: 'https://faro.codepresso.io/collect', env: 'live-us' },
};

/**
 * Returns the Faro receiver config matching the hostname. Callers pass
 * `window.location.hostname` on the client. Empty `url` => RUM disabled.
 */
export function resolveFaro(hostname: string): FaroConfig {
    return FARO[resolveRunType(hostname)];
}
